import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Award, Star, Crown, Zap, Swords } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { countryFlag } from "../lib/derive";
import { countryName } from "../lib/countries";
import { RankedTierChip } from "./Ranked";
import Avatar from "../components/Avatar";
import { UserName } from "../components/UserName";

function RankedLeaderboard({ mode, currentUserId }) {
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const [lb, m] = await Promise.all([
          api.get(`/leaderboards/ranked?mode=${mode}&limit=100`),
          api.get(`/leaderboards/ranked/me?mode=${mode}`),
        ]);
        if (!cancel) { setData(lb.data); setMine(m.data); }
      } catch (_) { /* ignore */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [mode]);

  if (loading) return <div className="text-zinc-500 text-sm">loading…</div>;
  if (!data) return null;
  const items = data.items || [];
  const daysLeft = data.season?.end_at
    ? Math.max(0, Math.round((new Date(data.season.end_at).getTime() - Date.now()) / 86400000))
    : null;

  return (
    <div className="space-y-4" data-testid={`ranked-leaderboard-${mode}`}>
      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-4 flex flex-wrap items-center gap-3">
        <Swords size={18} className="text-emerald-300" />
        <div>
          <div className="font-bold text-sm">{data.season?.name || "Active season"}</div>
          {daysLeft != null && (
            <div className="text-xs text-zinc-400">ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}</div>
          )}
        </div>
      </div>

      {mine && mine.global_rank ? (
        <div className="rounded-2xl border border-[var(--accent)]/30 bg-[var(--accent)]/5 p-4 flex flex-wrap items-center gap-3"
             data-testid={`ranked-me-banner-${mode}`}>
          <RankedTierChip tier={mine.tier} mmr={mine.mmr} />
          <span className="text-sm">
            You: <span className="font-bold">#{mine.global_rank}</span> of {mine.global_total || items.length}
            {mine.percentile != null && <span className="text-zinc-400"> · Top {mine.percentile}%</span>}
          </span>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-white/10 p-4 text-xs text-zinc-500">
          Not yet ranked — play your first ranked match
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center" data-testid={`ranked-empty-${mode}`}>
          <div className="text-base font-bold">Be the first to climb — play Ranked</div>
          <Link to="/ranked" className="mt-4 inline-block px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold">Open Ranked</Link>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[var(--surface)]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[var(--surface-2)] border-b border-white/10">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Rank</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Player</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Country</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">MMR</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Peak</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Games</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Wins</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Win %</th>
                <th className="px-3 py-2 text-[10px] uppercase text-zinc-500">Tier</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const wr = r.games_played ? Math.round((r.wins / r.games_played) * 100) : 0;
                const isMe = r.user_id === currentUserId;
                return (
                  <tr key={r.user_id} data-testid={`ranked-row-${r.rank}`}
                    className={`border-b border-white/5 hover:bg-white/5 ${isMe ? "bg-[var(--accent)]/8" : ""}`}>
                    <td className="px-3 py-2 text-zinc-400 font-mono tabular-nums">#{r.rank}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar user={r} size="sm" />
                        <UserName user={r} to={`/users/${r.user_id}`} className="font-semibold hover:text-[var(--accent)] truncate" />
                        {isMe && <span className="text-[9px] uppercase tracking-widest text-[var(--accent)] font-bold">(you)</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-zinc-400">{countryFlag(r.country)} {countryName(r.country) || "—"}</td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums">{r.mmr}</td>
                    <td className="px-3 py-2 text-right text-zinc-400 tabular-nums">{r.peak_mmr}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.games_played}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.wins}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{wr}%</td>
                    <td className="px-3 py-2"><RankedTierChip tier={r.tier} compact /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TIER_STYLES = {
  diamond:  { bg: "from-cyan-200/30 to-cyan-500/10", text: "text-cyan-200", ring: "ring-cyan-300/40", label: "Diamond" },
  platinum: { bg: "from-zinc-200/30 to-zinc-500/10", text: "text-zinc-200", ring: "ring-zinc-300/40", label: "Platinum" },
  gold:     { bg: "from-yellow-300/30 to-amber-600/10", text: "text-yellow-200", ring: "ring-yellow-300/40", label: "Gold" },
  silver:   { bg: "from-slate-300/30 to-slate-500/10", text: "text-slate-200", ring: "ring-slate-300/40", label: "Silver" },
  bronze:   { bg: "from-amber-600/30 to-amber-900/10", text: "text-amber-300", ring: "ring-amber-500/40", label: "Bronze" },
};

import { RankBadge, TIER_META } from "../components/RankBadge";
import { ModeratorBadge } from "../components/ModeratorBadge";

export function TierChip({ tier, rank, total, percentile, compact }) {
  const meta = TIER_META[tier] || TIER_META.bronze;
  return (
    <span
      data-testid={`tier-chip-${tier}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-black/30 ring-1 ring-white/10 px-2 py-1"
    >
      <RankBadge tier={tier} size="sm" animated showLabel={false} />
      <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.primary }}>
        {meta.label}
      </span>
      {!compact && rank ? (
        <span className="text-[10px] text-zinc-300">· #{rank}</span>
      ) : null}
      {!compact && percentile != null ? (
        <span className="text-[10px] text-zinc-400">· Top {percentile}%</span>
      ) : null}
    </span>
  );
}

function PodiumCard({ row, index, kind }) {
  const tiers = ["gold", "silver", "bronze"];
  const t = TIER_STYLES[tiers[index]] || TIER_STYLES.bronze;
  const score = kind === "weekly" ? row.weekly_score : row.football_iq;
  const scoreLabel = kind === "weekly" ? "weekly pts" : "Football IQ";
  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0, y: 10 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      transition={{ delay: index * 0.12, type: "spring", stiffness: 220, damping: 18 }}
      className={`rounded-2xl bg-gradient-to-br ${t.bg} ring-2 ${t.ring} p-5 text-center`}
      data-testid={`podium-${index + 1}`}
    >
      <div className={`text-[10px] uppercase tracking-[0.4em] font-bold ${t.text}`}>#{row.rank}</div>
      <div className="my-3 flex justify-center">
        <Avatar user={row} size="lg" />
      </div>
      <div className="text-base font-black truncate flex items-center gap-1.5 justify-center"><UserName user={row} /></div>
      <div className="text-[11px] text-zinc-400">{countryFlag(row.country)} {row.country || ""}</div>
      <div className="mt-3 text-3xl font-black tabular-nums">{score}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{scoreLabel}</div>
    </motion.div>
  );
}

function Row({ row, currentUserId, kind }) {
  const isMe = row.user_id === currentUserId;
  const score = kind === "weekly" ? row.weekly_score : row.football_iq;
  const games = kind === "weekly" ? row.weekly_games_played : row.games_played;
  const wins  = kind === "weekly" ? row.weekly_wins         : row.wins;
  return (
    <tr data-testid={`leaderboard-row-${row.rank}`}
      className={`border-b border-white/5 hover:bg-white/5 ${isMe ? "bg-[var(--accent)]/8" : ""}`}>
      <td className="px-3 py-2 text-zinc-400 font-mono tabular-nums">#{row.rank}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7"><Avatar user={row} size="sm" /></div>
          <UserName user={row} to={`/users/${row.user_id}`} className="font-semibold hover:text-[var(--accent)] truncate" />
          {isMe && <span className="text-[9px] uppercase tracking-widest text-[var(--accent)] font-bold">(you)</span>}
        </div>
      </td>
      <td className="px-3 py-2 text-zinc-400">{countryFlag(row.country)} {countryName(row.country) || "—"}</td>
      <td className="px-3 py-2 text-right tabular-nums font-bold">{score}</td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{games}</td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{wins}</td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{row.win_rate}%</td>
    </tr>
  );
}

export default function Leaderboards() {
  const { user } = useAuth();
  const [tab, setTab] = useState("global");
  const [data, setData] = useState({ items: [], total: 0 });
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (async () => {
      const url = tab === "global" ? "/leaderboards/global?limit=100" : "/leaderboards/weekly?limit=100";
      if (tab !== "global" && tab !== "weekly") { setLoading(false); return; }
      const [{ data: lb }, { data: meD }] = await Promise.all([
        api.get(url),
        api.get("/leaderboards/me"),
      ]);
      setData(lb); setMe(meD);
      setLoading(false);
    })();
  }, [tab]);

  const top3 = data.items.slice(0, 3);
  const rest = data.items.slice(3);
  const meRank = tab === "global" ? me?.global_rank : me?.weekly_rank;
  const meTotal = tab === "global" ? me?.global_total : me?.weekly_total;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="leaderboards-page">
      <header className="px-6 py-5 max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/dashboard" className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] hover:brightness-125">← dashboard</Link>
        <nav className="flex items-center gap-1 text-xs uppercase tracking-[0.3em]">
          <Link to="/ranked" className="px-3 py-1.5 rounded-full hover:bg-white/5 text-[var(--accent)]">Ranked</Link>
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-6 pb-16 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-4xl sm:text-5xl font-black">Hall of Fame</h1>
          <div className="inline-flex rounded-full bg-[var(--surface)] border border-white/10 p-1 flex-wrap" role="tablist">
            {[
              { k: "global", label: "Global" },
              { k: "weekly", label: "Weekly" },
              { k: "ranked_classic", label: "Ranked Classic" },
              { k: "ranked_career_path", label: "Ranked Career" },
              { k: "ranked_quiz", label: "Ranked Quiz" },
            ].map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} role="tab" aria-selected={tab === t.k}
                data-testid={`tab-${t.k}`}
                className={`px-5 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold transition ${
                  tab === t.k ? "bg-[var(--accent)] text-black" : "text-zinc-400 hover:text-white"
                }`}>{t.label}</button>
            ))}
          </div>
        </div>

        {(tab === "ranked_classic" || tab === "ranked_career_path" || tab === "ranked_quiz") ? (
          <RankedLeaderboard
            mode={tab === "ranked_classic" ? "classic" : tab === "ranked_career_path" ? "career_path" : "quiz"}
            currentUserId={user?.id}
          />
        ) : (
          <>
        {me?.global_rank > 0 && (
          <div className="rounded-2xl border border-[var(--accent)]/30 bg-[var(--accent)]/5 p-4 flex flex-wrap items-center gap-3"
            data-testid="me-rank-banner">
            <span className="text-sm">
              You: <span className="font-bold">#{meRank || "—"}</span> of {meTotal || 0}
              {tab === "global" && me.percentile_global != null && (
                <span className="text-zinc-400"> · Top {me.percentile_global}%</span>
              )}
            </span>
            <Link to="/profile" className="ml-auto text-xs text-[var(--accent)] hover:brightness-125 uppercase tracking-widest">My profile →</Link>
          </div>
        )}

        {loading ? (
          <div className="text-zinc-500 text-sm">loading…</div>
        ) : data.items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center" data-testid="empty-state">
            <div className="text-base font-bold">{tab === "weekly" ? "No games this week yet." : "No ranked players yet."}</div>
            <Link to="/dashboard" className="mt-4 inline-block px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold">Play a game</Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="podium">
              {top3.map((r, i) => <PodiumCard key={r.user_id} row={r} index={i} kind={tab} />)}
            </div>
            {rest.length > 0 && (
              <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[var(--surface)]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[var(--surface-2)] border-b border-white/10">
                    <tr>
                      <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Rank</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Player</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Country</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">{tab === "weekly" ? "Pts" : "IQ"}</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Games</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Wins</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Win %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rest.map((r) => <Row key={r.user_id} row={r} currentUserId={user?.id} kind={tab} />)}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        </>
        )}
      </main>
    </div>
  );
}
